import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { prefersReducedMotion } from '../../theme/motion';

// Elements that should morph between pages share a `view-transition-name`. Home
// tiles and the destination page header use `vtName(section, part)` for that.
// Only the icon and title morph: the glass tile itself has no counterpart on
// the next page, and a frosted (backdrop-filter) snapshot left fading on its
// own reads as a blurry box.
export const vtName = (section: string, part: 'icon' | 'title') => `vt-${section}-${part}`;

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => Promise<void> | void) => { finished: Promise<void> };
};

// Routes are lazy-loaded, so after navigating we wait for the destination to
// mount its marker (data-vt-ready) before the browser snapshots the new page.
// Otherwise the transition would capture the Suspense spinner.
function waitForPage(path: string, timeoutMs = 900): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now();
    const check = () => {
      const marker = document.querySelector(`[data-vt-ready="${path}"]`);
      if (marker || performance.now() - started > timeoutMs) resolve();
      // A timer rather than requestAnimationFrame: rAF pauses in background
      // tabs, which would leave the transition waiting until its own timeout.
      else window.setTimeout(check, 16);
    };
    check();
  });
}

export function useTransitionNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (path: string) => {
      const doc = document as ViewTransitionDocument;
      if (!doc.startViewTransition || prefersReducedMotion()) {
        navigate(path);
        return;
      }
      doc.startViewTransition(async () => {
        flushSync(() => navigate(path));
        await waitForPage(path);
      });
    },
    [navigate],
  );
}
